import { updateUser as _updateUser, UpdateUserFields } from '@griply/services';
import { Locales, PmfSurvey, UsageStatisticsEntry } from '@griply/types';
import { serverTimestamp, Timestamp, updateDoc } from 'firebase/firestore';
import {
  FirestoreWriteOp,
  logFirestoreWriteError,
} from 'shared/services/error-tracking';
import { DateFormatOptions } from 'shared/types/date-format-options';
import { ThemeModes } from 'shared/types/theme-modes';
import { FavoriteReference, Notifications } from 'shared/types/user-settings';
import { WeekDays } from 'shared/types/week-days';

import { getUser } from './get-user';
import { doc, getDatabase } from './helpers';

export type UserFields = {
  name: string;
  lastName: string;
  language: Locales;
  dateFormat: DateFormatOptions;
  startOfWeek: WeekDays;
  themePreference: ThemeModes;
  notifications: Notifications;
  favorites: FavoriteReference[];
  metaSource: string;
  pmfSurvey: Partial<PmfSurvey>;
  usageStatistics: UsageStatisticsEntry[];
};

const buildFields = (input: Partial<UserFields>): UpdateUserFields => {
  const {
    name,
    lastName,
    metaSource,
    language,
    dateFormat,
    startOfWeek,
    favorites,
    themePreference,
    notifications,
    pmfSurvey,
    usageStatistics,
  } = input;

  const settings: UpdateUserFields['settings'] = {};

  if (language) {
    settings.language = language;
  }

  if (dateFormat) {
    settings.dateFormat = dateFormat;
  }

  if (startOfWeek) {
    settings.startOfWeek = startOfWeek;
  }

  if (favorites) {
    settings.favorites = favorites;
  }

  if (themePreference) {
    settings.themePreference = { desktop: themePreference };
  }

  if (notifications) {
    settings.notifications = notifications;
  }

  return {
    name,
    lastName,
    metaSource,
    pmfSurvey,
    usageStatistics,
    settings: Object.keys(settings).length ? settings : undefined,
  };
};

export const updateUser = async (input: Partial<UserFields>) => {
  const user = await getUser();

  if (!user) {
    return;
  }

  const uid = user.uid;

  await _updateUser({
    userId: uid,
    fields: buildFields(input),
    db: getDatabase(),
    doc,
    updateDoc,
    serverTimestamp,
    timestampFromDate: (date: Date) => Timestamp.fromDate(date),
    onWriteError: (error) =>
      logFirestoreWriteError({
        op: FirestoreWriteOp.UpdateUser,
        error,
        uid,
        docPath: `users/${uid}`,
      }),
  });
};
